import { useEffect, useRef, useState } from "react";

/**
 * Texto que se revela carácter a carácter, como una máquina de escribir.
 *
 * Réplica web del `TypewriterText` de iOS. Recibe el texto completo y lo va
 * mostrando con `setInterval`; al terminar limpia el timer. Si el texto cambia
 * (p. ej. hidratación), reinicia.
 *
 * Pensado para el modo `typewriterReveal`.
 */
export interface TypewriterTextProps {
  text: string;
  /** Velocidad en ms por carácter. ~72ms ≈ 14 cps, más natural de leer. */
  speedMs?: number;
  /** Clases extra para el elemento de texto. */
  className?: string;
  onComplete?: () => void;
}

export default function TypewriterText({
  text,
  speedMs = 72,
  className = "",
  onComplete
}: TypewriterTextProps) {
  const [count, setCount] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setCount(0);
    if (!text) return;
    let i = 0;
    timer.current = setInterval(() => {
      i += 1;
      setCount(i);
      if (i >= text.length && timer.current) {
        clearInterval(timer.current);
        timer.current = null;
        onComplete?.();
      }
    }, speedMs);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [text, speedMs, onComplete]);

  return (
    <span className={className} aria-label={text}>
      {text.slice(0, count)}
      {/* Cursor parpadeante mientras escribe. */}
      {count < text.length && (
        <span className="ml-0.5 inline-block animate-pulse-soft" aria-hidden>
          ▍
        </span>
      )}
    </span>
  );
}
